import { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { SelectInput } from '../../../../components/common/select/SelectInput';
import {
	Css,
	IDropdownItem,
	ITheme,
	Responsive,
	StyledSelectMain,
} from '../../../../ui';
import {
	SOLICITATIONS_ALL_CATEGORIES,
	SOLICITATIONS_ALL_LOCATIONS,
	SOLICITATIONS_ALL_STATUSES,
	SOLICITATIONS_ALL_TYPES,
	SolicitationsFilterOptions,
	mapSolicitationFilterTypeToFilterItems,
	solicitationsFilterOptions,
} from '../../domain/models/SolicitationsFilterOption';
import { ISolicitationFilter } from '../../hooks/useSolicitationsFilter';
import { ISolicitationEntry } from '../../../../contentful/types/ISolicitationEntry';

interface ISolicitationsFiltersProps {
	onSelectFilter: (
		filterType: SolicitationsFilterOptions,
		selectedItem: string,
	) => void;
	state: ISolicitationFilter;
	solicitations: ISolicitationEntry[];
}

export default function SolicitationsFilter({
	onSelectFilter,
	state,
	solicitations,
}: ISolicitationsFiltersProps) {
	const isMobileXL = Responsive.useMatchesBreakpoint('mobileXL');
	const [showPlaceholder, setShowPlaceholder] = useState<{
		[key: string]: boolean;
	}>({});

	const filterRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

	const resetOptionsMap = {
		[SolicitationsFilterOptions.Type]: SOLICITATIONS_ALL_TYPES,
		[SolicitationsFilterOptions.Category]: SOLICITATIONS_ALL_CATEGORIES,
		[SolicitationsFilterOptions.Location]: SOLICITATIONS_ALL_LOCATIONS,
		[SolicitationsFilterOptions.Status]: SOLICITATIONS_ALL_STATUSES,
	};

	const handleSelectFilter = (
		filterOption: SolicitationsFilterOptions,
		selectedItem: string,
	) => {
		onSelectFilter(filterOption, selectedItem);
		setShowPlaceholder((prevState) => ({
			...prevState,
			[filterOption]: selectedItem !== resetOptionsMap[filterOption],
		}));
	};

	// Scroll select filter into view when it's in a horizontal scroll
	const handleOnSelectClick = (index: number) => {
		const filterElement = filterRefs.current[index];
		const parentElement = filterElement?.parentElement;

		if (!parentElement) return;

		const parentRect = parentElement.getBoundingClientRect();
		const filterRect = filterElement.getBoundingClientRect();

		const { scrollLeft } = parentElement;
		const targetScrollLeft =
			scrollLeft + filterRect.left - parentRect.left - 16;

		parentElement.scrollTo({
			left: targetScrollLeft,
			behavior: 'smooth',
		});
	};

	return (
		<SolicitationsFiltersContainer>
			<SolicitationsFiltersTitle>Filter Options</SolicitationsFiltersTitle>
			<SolicitationsFiltersWrap>
				{solicitationsFilterOptions.map((filterOption, index) => (
					<div
						key={filterOption}
						ref={(el) => {
							filterRefs.current[index] = el;
						}}
					>
						<StyledSelectInput
							key={filterOption}
							items={mapSolicitationFilterTypeToFilterItems(
								filterOption,
								solicitations,
							)}
							placeholder={filterOption}
							placeholderAsLabel={!!showPlaceholder[filterOption]}
							initialSelectedItem={solicitationsFilterOptions[index]}
							withDropdownFloatingPortal={isMobileXL}
							onClick={() => handleOnSelectClick(index)}
							selectedItem={state[filterOption]}
							onChange={(selectedItem: IDropdownItem | null) =>
								handleSelectFilter(filterOption, selectedItem as string)
							}
						/>
					</div>
				))}
			</SolicitationsFiltersWrap>
		</SolicitationsFiltersContainer>
	);
}

const SolicitationsFiltersContainer = styled.div`
	min-width: 100vw;
	margin-bottom: ${Css.pxToRem(40)};
`;

const SolicitationsFiltersWrap = styled.div<{}, ITheme>`
	display: flex;
	gap: ${Css.pxToRem(8)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		overflow-x: auto;
		overflow-y: hidden;
		margin-left: -${(props) => props.theme.layout.main.margin.mobile};
		padding-left: ${(props) => props.theme.layout.main.margin.mobile};
		padding-right: ${(props) => props.theme.layout.main.margin.mobile};
		width: 100%;
	}
`;

const SolicitationsFiltersTitle = styled.h5`
	margin-top: ${Css.pxToRem(32)};
	margin-bottom: ${Css.pxToRem(16)};
`;

const StyledSelectInput = styled(SelectInput)`
	${StyledSelectMain} {
		min-width: 140px;
		text-transform: capitalize;
	}

	svg {
		path {
			fill: currentColor !important;
		}
	}
`;
